import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { config } from '../../../../../config';
import UpdateWebinarInstructor from "./UpdateWebinarInstructor";

const ViewWebinarInstructors = () => {
  const apiUrl = config.apiUrl;
  const imageUrl = config.imageUrl;
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    image: null,
    headline: '',
    experience: '',
    goal: '',
  });
  const [currentImage, setCurrentImage] = useState('');

  const fetchInstructors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/webinarinstructors`);
      setInstructors(response.data.data || response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching instructors:', err);
      setError('Failed to load instructors. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstructors();
  }, [apiUrl]);

  const handleEditClick = (instructor) => {
    setEditId(instructor.id);
    setFormData({
      slug: instructor.slug,
      image: null,
      headline: instructor.headline,
      experience: instructor.experience,
      goal: instructor.goal,
    });
    setCurrentImage(instructor.image_url);
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setCurrentImage('');
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: files ? files[0] : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = new FormData();
      data.append('_method', 'PUT');
      data.append('slug', formData.slug);
      data.append('headline', formData.headline);
      data.append('experience', formData.experience);
      data.append('goal', formData.goal);
      
      if (formData.image) {
        data.append('image', formData.image);
      }

      await axios.post(`${apiUrl}/webinarinstructorsupdate/${editId}`, data, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });

      setEditId(null);
      setCurrentImage('');
      fetchInstructors();
    } catch (error) {
      console.error('Error updating instructor:', error);
      setError(error.response?.data?.message || 'Failed to update instructor');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this instructor?')) {
      try {
        await axios.delete(`${apiUrl}/webinarinstructordelete/${id}`);
        setInstructors(instructors.filter(instructor => instructor.id !== id));
      } catch (err) {
        console.error('Error deleting instructor:', err);
        setError('Failed to delete instructor. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Instructors</h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {instructors.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No instructors found.</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name/Slug
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Experience/goal
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {instructors.map((instructor) => (
                <tr key={instructor.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {instructor.image && (
                      <img 
                        src={`${imageUrl}/${instructor.image}`} 
                        alt={instructor.slug} 
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{instructor.headline}</div>
                    <div className="text-sm font-medium text-gray-900">{instructor.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{instructor.experience}</div>
                    <div className="text-sm text-gray-900">{instructor.goal}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEditClick(instructor)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(instructor.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editId && 
        <div className="mt-8">
           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateWebinarInstructor 
                formData={formData}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                currentImage={currentImage}
                onCancel={handleCancelEdit}
              />
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default ViewWebinarInstructors;